import { parseImage } from "./image";
import { isUuid } from "../misc";
import _ from "lodash";
import { parseDocumentation } from "./documentation";

export const parseApiProducts = (apiProducts, apiDocumentations) => {
  if (!apiProducts) return [];

  let productsArray;

  // Handle plain object
  if (typeof apiProducts === "object" && !Array.isArray(apiProducts)) {
    productsArray = Object.values(apiProducts);
  }
  // Handle plain array
  else if (Array.isArray(apiProducts)) {
    productsArray = apiProducts;
  } else {
    return [];
  }

  const result = productsArray.map((product) =>
    parseApiProduct(product, apiDocumentations)
  );
  return _.orderBy(result, ["weight", "title"], ["desc", "desc"]);
};

export const parseApiProductsCategory = (category) => {
  if (!category) return [];

  // Handle plain array (backend DTOs or already parsed)
  if (Array.isArray(category)) {
    return category
      .map((item) => ({
        name: item.name || item.Name || "",
        uuid: item.uuid || item.id || item.Id,
      }))
      .filter((cat) => cat.name && cat.uuid);
  }
  // Handle plain object
  else if (typeof category === "object") {
    return Object.values(category)
      .map((item) => ({
        name: item.name || item.Name || "",
        uuid: item.uuid || item.id || item.Id,
      }))
      .filter((cat) => cat.name && cat.uuid);
  }

  return [];
};

export const parseApiProductsResponse = (data) =>
  Object.keys(data)
    .map((id) => data[id])
    .filter((product) => {
      // Check for deprecated in both uppercase and lowercase
      const isDeprecated =
        product.deprecated !== undefined
          ? product.deprecated
          : product.Deprecated;
      return !isDeprecated;
    });

export const parseApiProduct = (apiProduct, apiDocumentations) => {
  if (!apiProduct) return null;

  // Direct property access for plain objects
  const id = apiProduct.id || apiProduct.Id;
  const productName = apiProduct.productName || apiProduct.ProductName;
  const productDescription =
    apiProduct.productDescription || apiProduct.ProductDescription;
  const productVersion = apiProduct.productVersion || apiProduct.ProductVersion;
  const isDeprecated =
    apiProduct.deprecated !== undefined
      ? apiProduct.deprecated
      : apiProduct.Deprecated;
  const productWeight = apiProduct.weight || apiProduct.Weight || 0;
  const urlAlias = apiProduct.urlAlias || apiProduct.UrlAlias;
  const productLabel = apiProduct.label || apiProduct.Label;
  const isCommercialProduct =
    apiProduct.commercialProduct || apiProduct.CommercialProduct;
  const disableDocumentation =
    apiProduct.disableDocumentation || apiProduct.DisableDocumentation;

  // Get API endpoints
  const apiEndpoints = apiProduct.apiEndpoints || apiProduct.ApiEndpoints || [];
  let apiEndpointsParsed = [];

  if (Array.isArray(apiEndpoints) && apiEndpoints.length > 0) {
    apiEndpointsParsed = apiEndpoints.map((endpoint) => {
      // Get urlAlias or try to extract from endpointUrls
      let alias = endpoint.urlAlias || endpoint.UrlAlias || "";

      // If no urlAlias, try to extract path from endpointUrls
      if (!alias) {
        const endpointUrls =
          endpoint.endpointUrls || endpoint.EndpointUrls || "";
        if (
          endpointUrls &&
          typeof endpointUrls === "string" &&
          endpointUrls.includes("developer.nbg.gr")
        ) {
          // Extract path from URL like "https://developer.nbg.gr/documentation/Account-Information-Berlin-Group-v1"
          // Result: "/documentation/Account-Information-Berlin-Group-v1"
          const parts = endpointUrls.split("developer.nbg.gr");
          if (parts.length > 1) {
            alias = parts[1];
          }
        }
      }

      return {
        name: endpoint.apiName || endpoint.ApiName || "API Documentation",
        title: endpoint.apiName || endpoint.ApiName || "API Documentation",
        alias: alias,
        uuid: endpoint.id || endpoint.Id || null,
        version: endpoint.apiVersion || endpoint.ApiVersion || "",
        deprecated: endpoint.deprecated || endpoint.Deprecated || false,
        description: endpoint.description || endpoint.Description || "",
        swagger: endpoint.swagger || endpoint.Swagger || null,
        documentation: endpoint.documentation || endpoint.Documentation || null,
      };
    });
  }

  // Parse features
  const productFeatures =
    apiProduct.productFeatures || apiProduct.ProductFeatures || [];
  const parsedFeatures = productFeatures.map((feature) => ({
    uuid: feature.id || feature.Id,
    title: feature.title || feature.Title || feature.FeatureTitle || "",
    description:
      feature.description ||
      feature.Description ||
      feature.FeatureDescription ||
      "",
    icon: parseImage(feature.icon || feature.Icon),
  }));

  // Parse advantages
  const productAdvantages =
    apiProduct.productAdvantages || apiProduct.ProductAdvantages || [];
  const parsedAdvantages = productAdvantages.map((advantage, index) => ({
    uuid: advantage.id || advantage.Id || `advantage-${id}-${index}`,
    title: advantage.title || advantage.Title || advantage.AdvantageTitle || "",
    description:
      advantage.description ||
      advantage.Description ||
      advantage.AdvantageDescription ||
      "",
  }));

  // Parse related products
  const relatedProducts =
    apiProduct.relatedProducts || apiProduct.RelatedProducts || [];
  const parsedRelated = relatedProducts.map((related) => {
    if (typeof related === "string") {
      return {
        uuid: related,
        title: "Related API Product",
        description: "Click to view details",
        alias: null,
      };
    }

    return {
      uuid: related.id || related.Id,
      title:
        related.productName || related.ProductName || "Related API Product",
      description:
        related.productDescription || related.ProductDescription || "",
      alias: related.urlAlias || related.UrlAlias || null,
      image: parseImage(related.productImage || related.ProductImage),
    };
  });

  // Parse images
  const productImage = apiProduct.productImage || apiProduct.ProductImage;
  const headerProductImage =
    apiProduct.headerProductImage || apiProduct.HeaderProductImage;

  // Parse categories
  const productCategories =
    apiProduct.productCategories || apiProduct.ProductCategories || [];
  const parsedCategories = productCategories.map((category) => {
    if (typeof category === "string") {
      return { name: category };
    }
    return {
      name: category.name || category.Name || category.CategoryName || "",
      uuid:
        category.id || category.Id || category.uuid || category.Uuid || null,
    };
  });

  // Parse comparison table with direct property access
  const comparisonTable =
    apiProduct.comparisonTable || apiProduct.ComparisonTable;
  const comparisonApis = comparisonTable?.Apis || comparisonTable?.apis || [];
  const comparisonProductNames =
    comparisonTable?.ProductNames || comparisonTable?.productNames || [];
  const comparisonTableData =
    comparisonTable?.Table || comparisonTable?.table || [];

  // Parse changelog
  const changeLog = parseChangeLog(
    apiProduct.changeLog || apiProduct.ChangeLog || []
  );

  // Build result object
  const result = {
    uuid: id,
    title: productName,
    description: productDescription || "",
    version: productVersion,
    deprecated: isDeprecated || false,
    weight: productWeight,
    alias: urlAlias,
    label: productLabel,
    commercialProduct: isCommercialProduct || false,
    disable_documentation: disableDocumentation || false,
    overviewDisplay: true, // Default value
    image: parseImage(productImage),
    headerImage: parseImage(headerProductImage),
    apis: apiEndpointsParsed,
    features: parsedFeatures,
    advantages: parsedAdvantages,
    related: parsedRelated,
    productCategories: parsedCategories,
    changeLog: changeLog,
    subscribed: apiProduct.subscribed || false,
    comparisonTable: {
      apis: parseComparisonTablesApis(comparisonApis),
      productNames: parseComparisonTablesApis(comparisonProductNames),
      table: parseComparisonTables(comparisonTableData),
    },
    advantagesHeader:
      apiProduct.advantagesHeader || apiProduct.AdvantagesHeader
        ? {
            title: apiProduct.advantagesHeader || apiProduct.AdvantagesHeader,
            body: "",
            id: id + "_advantages_header",
          }
        : null,
  };

  return result;
};

// Note: This function is currently unused but kept for potential future use
const parseFeatures = (items) => {
  if (!items) return [];
  const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
  return itemsArray.map((item) => ({
    uuid: item.uuid,
    icon: parseImage(item.icon),
    description: item.description,
    title: item.title,
  }));
};

const parseComparisonTables = (items) => {
  if (!items) return [];

  // Handle plain array/object
  const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
  return itemsArray.map((item) => ({
    type: item.type,
    data: {
      title: item.data && item.data.title,
      values:
        item.data && item.data.values
          ? Array.isArray(item.data.values)
            ? item.data.values
            : Object.values(item.data.values)
          : [],
    },
  }));
};

const parseComparisonTablesApis = (items) => {
  if (!items) return [];
  // Handle plain array/object
  return Array.isArray(items) ? items : Object.values(items || {});
};

// Note: This function is currently unused but kept for potential future use
const parseAdvantages = (items) => {
  if (!items) return [];
  const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
  return itemsArray.map((item) => ({
    uuid: item.uuid,
    description: item.description,
    title: item.title,
  }));
};

// Note: This function is currently unused but kept for potential future use
const parseCategories = (items) => {
  if (!items) return [];
  const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
  return itemsArray.map((item) => ({
    name: typeof item === "string" ? item : item.name,
  }));
};
// Note: This function is currently unused but kept for potential future use
const parseRelated = (items) => {
  if (!items) return [];
  const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
  return itemsArray.map((item) => {
    // For items with only uuid (from backend), return minimal object
    if (typeof item === "string" || (item.uuid && !item.title)) {
      return {
        uuid: typeof item === "string" ? item : item.uuid,
        description: "",
        title: "Related Product",
        alias: "",
        deprecated: false,
        image: null,
      };
    }
    // CHECK IF THE API VERSION IS DEPRECATED FROM URL
    const apiVersion = item.alias && item.alias.match(/.+?(?=-v[1-9])/);
    return {
      uuid: item.uuid,
      description: item.description || "",
      title: item.title || "Related Product",
      alias: apiVersion ? apiVersion[0] : item.alias || "",
      deprecated: item.deprecated || false,
      image: item.image || null,
    };
  });
};

export const getApiProductUuid = (props) => {
  const { apiProductId, viewMode } = props.match.params;
  if (isUuid(apiProductId)) {
    return apiProductId;
  } else {
    const apiProducts = parseApiProducts(props.apiProducts);

    const url = props.match.url;
    const alias = viewMode
      ? url.substr(0, url.length - viewMode.length - 1)
      : url;

    const apiProduct = _.find(apiProducts, { alias });
    return apiProduct ? apiProduct.uuid : null;
  }
};

export const getApiProduct = (props) => {
  const { apiDocumentations, apiProducts } = props;
  const apiProductUuid = getApiProductUuid(props);
  // Handle plain object
  const product = apiProducts ? apiProducts[apiProductUuid] : null;

  if (!product) return null;

  // Check if product is already parsed (has uuid field from parseApiProduct)
  // If it has uuid, it's already been through parseApiProduct via mapProductData
  if (product.uuid) {
    return product;
  }

  // Otherwise, it's raw backend data that needs parsing
  const parsed = parseApiProduct(product, apiDocumentations);
  return parsed;
};

// Changelog release dates are stored in UTC without a timezone marker ("2021-01-27T22:00:00"),
// and the browser reads such a value as local time - so midnight 28 Jan in Athens showed as
// 27 Jan. A value without "Z" or an offset is read as UTC.
const parseReleaseDate = (value) => {
  if (!value) return new Date();
  if (
    typeof value === "string" &&
    !/(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim())
  ) {
    return new Date(value.trim() + "Z");
  }
  return new Date(value);
};

export const parseChangeLog = (items) => {
  if (!items) return [];

  // Handle plain array
  if (Array.isArray(items)) {
    return items.map((item) => ({
      // Some versions were stored with a trailing space ("v2.7 "), which broke the sorting.
      version: (item.version || item.Version || "").toString().trim(),
      notes: item.notes || item.Notes || "",
      release_date: parseReleaseDate(item.releaseDate || item.release_date),
    }));
  }

  return [];
};

export const getUuidFromHistory = (prefix, history) => {
  if (history.location.pathname.includes(prefix)) {
    const suffix = history.location.pathname.replace(prefix, "");
    return isUuid(suffix) ? suffix : null;
  }
  return null;
};

export const getApiProductFromHistory = (apiProducts, history) => {
  const alias = history.location.pathname;
  return apiProducts.find((product) => product.alias === alias);
};

export const getApiProductUuidFromHistory = (apiProducts, uuid) => {
  return apiProducts.find((product) => product.uuid === uuid);
};

export const categoriesParser = (payload) => {
  if (!payload) {
    return [];
  }

  // Handle array of backend DTOs directly
  if (Array.isArray(payload)) {
    return payload
      .map((category) => ({
        name: category.name || category.Name || "",
        uuid: category.name || category.Name || category.id || category.Id, // Use name as UUID first
      }))
      .filter((cat) => cat.name); // Only require name since it's used as UUID
  }

  // Handle legacy JSON:API format for backward compatibility
  if (payload.data && Array.isArray(payload.data)) {
    const categories = [];
    payload.data.forEach((datum) => {
      const { name } = datum.attributes || {};
      if (name) {
        categories.push({ name, uuid: datum.id });
      }
    });
    return categories;
  }

  return [];
};
